import { useDeleteDocument } from "@features/ax/hooks";
import { DynamicForm } from "@shared/components/DynamicForm";
import { FormField, FormService } from "@shared/services/FormServices";
import { Button } from "@shared/ui/button";
import { FIELD_CLASSES } from "@shared/utils/constants";
import { getRemoteUtilityId } from "@shared/utils/getUtilityId";
import { downloadFileByUrl } from "@shared/utils/utils";
import { Download, FileText, X } from "lucide-react";
import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { useAddDocuments, useDocuments } from "../../hooks";

interface DocumentationStepProps {
  editMode: boolean;
  onNext?: () => void;
  onPrevious?: () => void;
  isFirstStep?: boolean;
  isLastStep?: boolean;
  setValidationError?: (error: string) => void;
  clearValidationError?: () => void;
}

const DocumentationStep: React.FC<DocumentationStepProps> = ({
  editMode,
  onNext,
  onPrevious,
  isFirstStep,
  setValidationError,
  clearValidationError,
}) => {
  const { id, action } = useParams();
  const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);
  const [documentNotes, setDocumentNotes] = useState<string>("");
  const dataLoadedRef = useRef(false);
  const addDocumentsMutation = useAddDocuments();
  const { mutateAsync: removeDocument, isPending: isDeleting } =
    useDeleteDocument("network");

  const {
    data: existingDocuments,
    isLoading: documentsLoading,
    refetch: refetchDocuments,
  } = useDocuments({
    networkId: id || "",
  });

  useEffect(() => {
    if (id) {
      refetchDocuments();
      dataLoadedRef.current = false;
    }
  }, [id, refetchDocuments]);

  useEffect(() => {
    if (action === "edit" && existingDocuments && !dataLoadedRef.current) {
      if (existingDocuments?.documentNotes) {
        setDocumentNotes(existingDocuments.documentNotes);
        form.setValue("documentNotes", existingDocuments.documentNotes);
      }

      dataLoadedRef.current = true;
    }
  }, [action, existingDocuments]);

  const removeExistingDocument = (documentId: string) => {
    removeDocument(documentId);
  };

  const handleNext = async () => {
    const remoteUtilityId = getRemoteUtilityId();
    try {
      clearValidationError?.();

      const formData = new FormData();
      uploadedFiles.forEach((file) => {
        formData.append("doc", file);
      });

      formData.append("remote_utility_id", remoteUtilityId.toString());

      await addDocumentsMutation.mutateAsync({
        networkId: id || "",
        payload: formData,
      });

      onNext?.();
    } catch (error) {
      setValidationError?.("Failed to upload documents. Please try again.");
    }
  };

  const formFields: FormField[] = [
    {
      name: "documents",
      type: "multi-file",
      label: "Network Documents",
      className: "col-span-2",
      placeholder: "Upload network documents",
      required: false,
      multiple: true,
      accept: ".pdf,.doc,.docx,.jpg,.jpeg,.png,.mp4",
      helperText:
        "Examples: Network Diagrams, Configuration Files, Documentation, Videos",
      classes: FIELD_CLASSES,
      maxFiles: 10,
      maxSize: 10,
    },
  ];

  const form = FormService.createForm({
    fields: formFields,
    defaultValues: {
      documents: uploadedFiles,
      documentNotes: documentNotes,
    },
  });

  const handleFieldChange = (fieldName: string, value: any) => {
    if (fieldName === "documents") {
      setUploadedFiles(value || []);
    }
    if (fieldName === "documentNotes") {
      setDocumentNotes(value || "");
    }
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6">
      <div className="mb-6">
        <h2 className="text-xl font-semibold text-gray-900">Documentation</h2>
        <p className="text-sm text-gray-600 mt-2">
          Upload relevant documents, drawings, or videos (optional).
        </p>
      </div>

      <div className="space-y-8">
        <div>
          <h3 className="text-lg font-medium mb-4">Documentation</h3>
          <DynamicForm
            fields={formFields}
            form={form}
            onFieldChange={handleFieldChange}
            config={{
              layout: "vertical",
              showSubmitButton: false,
              showResetButton: false,
              spacing: "medium",
            }}
          />
        </div>

        {existingDocuments?.result?.length > 0 && (
          <div>
            <p className="text-base font-medium mb-2">Uploaded Documents</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {existingDocuments?.result?.map((document: any) => (
                <div
                  key={document.id}
                  className="flex items-center justify-between p-3 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100"
                >
                  <div className="flex items-center space-x-3 flex-1 min-w-0">
                    <div className="flex-shrink-0">
                      <FileText className="w-5 h-5 text-blue-500" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {document.fileName || "Document"}
                      </p>
                    </div>
                  </div>
                  <div className="flex justify-end">
                    <Button
                      title="Download"
                      variant="ghost"
                      onClick={() => downloadFileByUrl(document.doc)}
                    >
                      <Download className="w-4 h-4" />
                    </Button>
                    <Button
                      onClick={() => removeExistingDocument(document.id)}
                      title="Remove"
                      variant="ghost"
                      isLoading={isDeleting}
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="flex justify-between mt-6 pt-6 border-t border-gray-200">
        <Button onClick={onPrevious} disabled={isFirstStep} variant="outline">
          Previous
        </Button>
        <Button onClick={handleNext} isLoading={addDocumentsMutation.isPending}>
          {editMode ? "Submit" : "Submit"}
        </Button>
      </div>
    </div>
  );
};

export default DocumentationStep;
